import React, { startTransition, useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import Popup from "../components/PopUP";
import "./SubscriptionManagement.css";
import api from "../Api";

const clonePlans = (plans) => JSON.parse(JSON.stringify(plans));

const createId = (prefix) =>
  `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const isAddOnPlan = (plan) => {
  const name = String(plan.name || plan.displayName || "").toLowerCase();
  return plan.is_add_on === true || /add[\s_-]?on/.test(name);
};

function AddOnManagement() {
  const showAddOns = true;
  const [savedPlans, setSavedPlans] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [isNewPlan, setIsNewPlan] = useState(false);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoading(true);
      setPlans([]);
      setSavedPlans([]);
      setSelectedPlanId(null);
      setIsNewPlan(false);
      try {
        const response = await api.get("/user-service/subscription/plans");
        const { data } = response.data;

        const mapped = Array.isArray(data)
          ? data.filter((plan) => isAddOnPlan(plan) === showAddOns).map((plan) => ({
            id: plan.id,
            displayName: plan.name,
            description: `${plan.name} plan — ${plan.billing_cycle} billing.`,
            priceInfo: `$${plan.price} / ${plan.billing_cycle.replace("ly", "")}`,
            billingNote: `Billed ${plan.billing_cycle}`,
            active: true,
            highlighted: false,
            platforms: ["iOS", "Android", "Web"],
            audience: "General",
            credit: plan.credit,
            token: plan.token,
            price: plan.price,
            currency: plan.currency,
            billing_cycle: plan.billing_cycle,
            stripe_price_id: plan.stripe_price_id || "",
            product_id_apple: plan.product_id_apple || "",
            product_id_google: plan.product_id_google || "",
            appleProductId: "",
            googleProductId: "",
            webProductId: plan.stripe_price_id || "",
            activate_at: plan.activate_at || "2026-04-25 10:10",
            deactivate_at: plan.deactivate_at || "2026-05-1 10:20",
            is_add_on: isAddOnPlan(plan)
          }))
          : [];

        setSavedPlans(clonePlans(mapped));
        setPlans(mapped);
        setSelectedPlanId(mapped[0]?.id || null);
      } catch (error) {
        console.error("Unable to restore subscription plans:", error);
        setToast({
          message: "Saved plans could not be restored.",
          type: "warning",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [showAddOns]);

  useEffect(() => {
    if (!plans.length) return;
    const planExists = plans.some((plan) => plan.id === selectedPlanId);
    if (!planExists) setSelectedPlanId(plans[0].id);
  }, [plans, selectedPlanId]);

  const selectedPlan =
    plans.find((plan) => plan.id === selectedPlanId) || plans[0] || null;
  const hasUnsavedChanges =
    JSON.stringify(plans) !== JSON.stringify(savedPlans);

  const updateSelectedPlan = (updater) => {
    setPlans((currentPlans) =>
      currentPlans.map((plan) =>
        plan.id === selectedPlanId ? updater(plan) : plan
      )
    );
  };

  const handlePlanFieldChange = (field, value) => {
    updateSelectedPlan((plan) => ({ ...plan, [field]: value }));
  };

  const handleCreatePlan = () => {
    // Prevent creation of a new plan if there is already an unsaved new plan
    if (isNewPlan) {
      setToast({
        message: "Please save the current new plan before creating another.",
        type: "warning",
      });
      return;
    }

    const nextPlan = {
      id: createId("plan"),
      displayName: "",
      description: "",
      priceInfo: "",
      billingNote: "Billed monthly",
      active: true,
      highlighted: false,
      platforms: ["Web"],
      audience: "General",
      credit: "",
      token: "",
      price: "",
      currency: "USD",
      billing_cycle: "monthly",
      stripe_price_id: "",
      appleProductId: "",
      googleProductId: "",
      webProductId: "",

      product_id_apple: "",
      product_id_google: "",
      activate_at: "",
      deactivate_at: "",
      is_add_on: showAddOns
    };

    setPlans((prev) => [...prev, nextPlan]);
    startTransition(() => setSelectedPlanId(nextPlan.id));
    setIsNewPlan(true); // mark as unsaved new plan
  };

  const handleSave = () => {
    if (!selectedPlan) return;
    setShowSaveConfirmModal(true);
  };

  const confirmSave = async () => {
    if (!selectedPlan) return;
    setShowSaveConfirmModal(false);
    setSaving(true);

    const payload = {
      name: selectedPlan.displayName,
      price: Number(selectedPlan.price),
      currency: selectedPlan.currency || "USD",
      billing_cycle: selectedPlan.billing_cycle || "monthly",
      credit: Number(selectedPlan.credit),
      token: Number(selectedPlan.token),
      stripe_price_id: selectedPlan.stripe_price_id || "",
      product_id_apple: selectedPlan.product_id_apple || "",
      product_id_google: selectedPlan.product_id_google || "",
      activate_at: selectedPlan.activate_at || null,
      deactivate_at: selectedPlan.deactivate_at || null,
      is_add_on: showAddOns,
    };

    try {
      if (isNewPlan) {
        const response = await api.post("/user-service/subscription/plan", payload);
        const saved = response.data.data;

        const mappedPlan = {
          ...selectedPlan,
          id: saved.id,
          displayName: saved.name,
          price: saved.price,
          currency: saved.currency,
          billing_cycle: saved.billing_cycle,
          credit: saved.credit,
          token: saved.token,
          stripe_price_id: saved.stripe_price_id || "",
          product_id_apple: saved.product_id_apple || "",
          product_id_google: saved.product_id_google || "",
          activate_at: saved.activate_at || "",
          deactivate_at: saved.deactivate_at || "",
          is_add_on: isAddOnPlan(saved),
          priceInfo: `$${saved.price} / ${saved.billing_cycle.replace("ly", "")}`,
          billingNote: `Billed ${saved.billing_cycle}`,
        };

        setPlans((prev) =>
          prev.map((p) => (p.id === selectedPlanId ? mappedPlan : p))
        );
        setSavedPlans((prev) => {
          const exists = prev.some((p) => p.id === mappedPlan.id);
          return exists
            ? prev.map((p) => (p.id === mappedPlan.id ? clonePlans([mappedPlan])[0] : p))
            : [...prev, clonePlans([mappedPlan])[0]];
        });

        startTransition(() => setSelectedPlanId(saved.id));
        setIsNewPlan(false);
        setToast({ message: "New plan created successfully.", type: "success" });

      } else {
        await api.patch(`/user-service/subscription/plan/${selectedPlan.id}`, payload);
        setSavedPlans(clonePlans(plans));
        setToast({ message: "Plan saved successfully.", type: "success" });
      }

    } catch (error) {
      console.error("Failed to save plan:", error);
      setToast({ message: "Failed to save plan. Please try again.", type: "warning" });
    } finally {
      setSaving(false);
    }
  };

  // const handleDeletePlan = () => {
  //   if (!selectedPlan) return;
  //   if (plans.length === 1) {
  //     setToast({
  //       message: "At least one subscription plan should remain available.",
  //       type: "warning",
  //     });
  //     return;
  //   }
  //   const remainingPlans = plans.filter((plan) => plan.id !== selectedPlan.id);
  //   setPlans(remainingPlans);
  //   startTransition(() => setSelectedPlanId(remainingPlans[0].id));
  // };

  const handleDiscardChanges = () => {
    if (isNewPlan) {
      // Remove the unsaved new plan from the list
      const remaining = plans.filter((p) => p.id !== selectedPlanId);
      setPlans(remaining);
      if (remaining.length > 0) {
        startTransition(() => setSelectedPlanId(remaining[0].id));
      }
      setIsNewPlan(false);
    } else {
      setPlans(clonePlans(savedPlans));
    }
    setToast({ message: "Changes discarded.", type: "info" });
  };

  if (loading) {
    return (
      <div className="subscription-management-page">
        <Sidebar />
        <div className="main-wrapper">
          <Header />
          <div className="content-wrapper">
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
          </div>
        </div>
      </div>
    );
  }

  // "2026-04-25 10:10" → "2026-04-25T10:10" (for input)
  const toDateTimeLocal = (value) => {
    if (!value) return "";
    return value.replace(" ", "T");
  };

  // "2026-04-25T10:10" → "2026-04-25 10:10" (for API)
  // const fromDateTimeLocal = (value) => {
  //   if (!value) return "";
  //   return value.replace("T", " ");
  // };

  return (
    <div className="subscription-management-page">
      <Popup
        message={toast?.message}
        type={toast?.type}
        onClose={() => setToast(null)}
        duration={3200}
      />
      <div
        className={`modal fade ${showSaveConfirmModal ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Save Action</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => setShowSaveConfirmModal(false)}
                disabled={saving}
              ></button>
            </div>
            <div className="modal-body">
              <p>
                Do you want to save changes for{" "}
                <strong>{selectedPlan?.displayName || "this plan"}</strong>?
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowSaveConfirmModal(false)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmSave}
                disabled={saving}
              >
                Yes, Save
              </button>
            </div>
          </div>
        </div>
      </div>
      <Sidebar />

      <div className="main-wrapper">
        <Header />

        <div className="content-wrapper">
          <section className="main-section spacer-y">
            <div className="container">
              <div className="row gy-4">
                <div className="col-xl-12">
                  <div className="common_main_bg_wrapper subscription-hero">
                    <div className="common_main_heading_wrapper">
                      <div className="vstack subscription-heading-copy">
                        <h1>{showAddOns ? "Add-on Packs" : "Subscription Plans"}</h1>
                        <p className="subscription-hero-copy">
                          {showAddOns
                            ? "Manage extra credit and token packs separately from base subscription plans."
                            : "Edit feature limits, plan labels and plan visibility from one place. App Store and Play Console pricing can stay managed outside the admin"}
                        </p>
                      </div>
                      <button
                        type="button"
                        className="outline-btn rounded-pill"
                        onClick={handleCreatePlan}
                        disabled={saving}
                      >
                        {saving ? "Creating..." : showAddOns ? "+ Add New add-on" : "+ Add New plan"}
                      </button>
                    </div>

                    <div className="subscription-hero-stats">
                      <div className="subscription-stat-pill">
                        <span>{plans.length}</span>
                        <small>{showAddOns ? "Add-ons configured" : "Plans configured"}</small>
                      </div>
                      <div
                        className={`subscription-stat-pill ${hasUnsavedChanges ? "is-alert" : ""
                          }`}
                      >
                        <span>{hasUnsavedChanges ? "Unsaved" : "Synced"}</span>
                        <small>
                          {hasUnsavedChanges
                            ? "Save to keep these edits"
                            : "Local draft matches saved state"}
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="subscription-plan-grid mt-4">
                    {plans.map((plan) => (
                      <button
                        key={plan.id}
                        type="button"
                        className={`subscription-plan-card ${selectedPlanId === plan.id ? "is-selected" : ""
                          } ${plan.highlighted ? "is-highlighted" : ""}`}
                        onClick={() => {
                          startTransition(() => setSelectedPlanId(plan.id));
                          setIsNewPlan(false); // switching away resets new-plan mode
                        }}
                      >
                        <div className="subscription-plan-card-top">
                          <div>
                            <h2 className="text-capitalize">{plan.displayName}</h2>
                            <p>{plan.priceInfo}</p>
                          </div>
                          <span
                            className={`subscription-plan-status ${plan.active ? "is-active" : "is-inactive"
                              }`}
                          >
                            {plan.active ? "Active" : "Deactive"}
                          </span>
                        </div>

                        <p className="subscription-plan-description">
                          {/* {plan.description} */}
                        </p>

                        <div className="subscription-platforms">
                          {plan.platforms.map((platform) => (
                            <span key={platform} className="platform-chip">
                              {platform}
                            </span>
                          ))}
                        </div>

                        <div className="subscription-feature-preview">
                          <div className="subscription-feature-row">
                            <span>Credits</span>
                            <strong>
                              {plan.credit !== "" ? Number(plan.credit).toLocaleString() : "—"}
                            </strong>
                          </div>
                          <div className="subscription-feature-row">
                            <span>Tokens</span>
                            <strong>
                              {plan.token !== "" ? Number(plan.token).toLocaleString() : "—"}
                            </strong>
                          </div>

                          {/* Show activate_at only if it's in the future */}
                          {plan.activate_at && new Date(plan.activate_at) > new Date() && (
                            <div className="subscription-feature-row">
                              <span>Updating On</span>
                              <strong>
                                {new Date(plan.activate_at).toLocaleString(undefined, {
                                  dateStyle: "medium",
                                  timeStyle: "short",
                                })}
                              </strong>
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>

                  {selectedPlan && (
                    <div className="subscription-editor mt-4">
                      <div className="subscription-editor-header">
                        <div>
                          <p className="subscription-editor-eyebrow">
                            {isNewPlan
                              ? `New ${showAddOns ? "add-on" : "plan"} - not saved yet`
                              : `Editing ${showAddOns ? "add-on" : "plan"}`}
                          </p>
                          <h2>{selectedPlan.displayName}</h2>
                          {/* <p className="subscription-editor-subcopy">
                            Update display text, product identifiers and limits
                            for this subscription.
                          </p> */}
                        </div>

                        {/* <div className="subscription-editor-actions">
                          <button
                            type="button"
                            className="subscription-delete-btn rounded-pill"
                            onClick={handleDeletePlan}
                          >
                            Disable
                          </button>
                        </div> */}
                      </div>

                      <div className="subscription-editor-grid">
                        <div className="subscription-panel subscription-panel-wide">
                          <div className="subscription-panel-heading">
                            <h3>Plan Details</h3>
                            {/* <p>Display copy and how this plan appears to users.</p> */}
                          </div>

                          <div className="row g-3">
                            {isNewPlan ?
                              <div className="col-md-6">
                                <label className="subscription-label">Plan name</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={selectedPlan.displayName}
                                  onChange={(e) => handlePlanFieldChange("displayName", e.target.value)}
                                />
                              </div>
                              : ""}

                            <div className="col-md-6">
                              <label className="subscription-label">Price ($)</label>
                              <input
                                type="text"
                                inputMode="decimal"
                                pattern="^\d+(\.\d{0,2})?$"
                                className="form-control"
                                value={
                                  selectedPlan.price !== undefined && selectedPlan.price !== null
                                    ? String(selectedPlan.price)
                                    : ""
                                }
                                onChange={(e) => {
                                  // Only allow numbers and up to 2 decimals
                                  let value = e.target.value;
                                  if (value === "") {
                                    handlePlanFieldChange("price", "");
                                  } else if (/^\d+(\.\d{0,2})?$/.test(value)) {
                                    handlePlanFieldChange("price", value);
                                  }
                                }}
                                maxLength={10}
                                placeholder="0.00"
                              />
                     
                            </div>

                            <div className="col-md-6">
                              <label className="subscription-label">Currency</label>
                              <input
                                type="text"
                                className="form-control readonly"
                                value={selectedPlan.currency}
                                onChange={(e) =>
                                  handlePlanFieldChange("currency", e.target.value)
                                }
                                readOnly
                              />
                            </div>
                            {!showAddOns && (
                              <div className="col-md-6">
                                <label className="subscription-label">Billing cycle</label>
                                <select
                                  className="form-control"
                                  value={selectedPlan.billing_cycle}
                                  onChange={(e) => handlePlanFieldChange("billing_cycle", e.target.value)}
                                >
                                  <option value="monthly">Monthly</option>
                                  <option value="yearly">Yearly</option>
                                </select>
                              </div>
                            )}
                            <div className="col-md-6">
                              <label className="subscription-label">Credits</label>
                              <input
                                type="number"
                                min={1}
                                className="form-control"
                                value={selectedPlan.credit}
                                onChange={(e) =>
                                  handlePlanFieldChange("credit", e.target.value)
                                }

                                disabled={selectedPlan.credit === 0}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="subscription-label" >Tokens</label>
                              <input
                                type="number"
                                min={1}
                                className="form-control"
                                value={selectedPlan.token}
                                onChange={(e) =>
                                  handlePlanFieldChange("token", e.target.value)
                                }

                                disabled={selectedPlan.token === 0}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="subscription-label">Stripe Price ID</label>
                              <input
                                type="text"
                                className="form-control"
                                value={selectedPlan.stripe_price_id}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\s/g, "");
                                  handlePlanFieldChange("stripe_price_id", value)
                                }}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="subscription-label">Google Plan ID</label>
                              <input
                                type="text"
                                className="form-control"
                                value={selectedPlan.product_id_google}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\s/g, "");
                                  handlePlanFieldChange("product_id_google", value)
                                }}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="subscription-label">Apple Plan ID</label>
                              <input
                                type="text"
                                className="form-control"
                                value={selectedPlan.product_id_apple}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\s/g, "");
                                  handlePlanFieldChange("product_id_apple", value);
                                }}
                              />
                            </div>

                            <div className="col-md-6">
                              <label className="subscription-label">Deactivate at</label>
                              <input
                                type="datetime-local"
                                className="form-control"
                                value={toDateTimeLocal(selectedPlan.deactivate_at)}
                                onChange={(e) => handlePlanFieldChange("deactivate_at", e.target.value)}
                              />
                            </div>
                            <div className="col-md-6">
                              <label className="subscription-label">Activate at</label>
                              <input
                                type="datetime-local"
                                className="form-control"
                                value={toDateTimeLocal(selectedPlan.activate_at)}
                                min={new Date().toISOString().slice(0, 16)}
                                onChange={(e) => handlePlanFieldChange("activate_at", e.target.value)}
                              />
                            </div>

                            {/* <div className="col-12">
                              <label className="subscription-label">Description</label>
                              <textarea
                                rows="3"
                                className="form-control"
                                value={selectedPlan.description}
                                onChange={(e) =>
                                  handlePlanFieldChange("description", e.target.value)
                                }
                              />
                            </div> */}
                          </div>
                        </div>
                      </div>

                      <div className="subscription-footer">
                        <button
                          type="button"
                          className="outline-btn rounded-pill"
                          onClick={handleDiscardChanges}
                          disabled={!hasUnsavedChanges || saving}
                        >
                          Discard changes
                        </button>
                        <button
                          type="button"
                          className="primary-btn rounded-pill"
                          onClick={handleSave}
                          disabled={saving}
                        >
                          {saving ? "Saving…" : "Save changes"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default AddOnManagement;
